import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerTierRuleToken, EnumToken } from '@sleed/bizcommerce-api';
import { customerTierRulesApi, customerTiersApi } from '~/lib/api/client';
import { formToToken, type CustomerTierRuleFormValues } from '~/lib/schemas/customer-tier-rule';

export const tierRuleKeys = {
	all: ['customer-tier-rules'] as const,
	list: () => [...tierRuleKeys.all, 'list'] as const,
	criteriaTypes: () => ['customer-tier-criteria-types'] as const
};

/** All tier rules (client-side grid; legacy loads the full set via GetAll). */
export function useCustomerTierRulesList() {
	return useQuery<CustomerTierRuleToken[]>({
		queryKey: tierRuleKeys.list(),
		queryFn: async () => (await customerTierRulesApi.getAll()).data.items ?? []
	});
}

/** Tier criteria types lookup (LoyaltyPoints / TotalPurchases / …). */
export function useCustomerTierCriteriaTypes() {
	return useQuery<EnumToken[]>({
		queryKey: tierRuleKeys.criteriaTypes(),
		queryFn: async () => (await customerTiersApi.getCustomerTierCriteriaTypes()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useCustomerTierRuleMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: tierRuleKeys.all });
	const create = useMutation({
		mutationFn: async (form: CustomerTierRuleFormValues) => (await customerTierRulesApi.insert(formToToken(form, null))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async ({ form, original }: { form: CustomerTierRuleFormValues; original: CustomerTierRuleToken | null }) =>
			(await customerTierRulesApi.update(formToToken(form, original))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await customerTierRulesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
